import { useEffect, useState, useMemo } from 'react';
import { Page, Table, SearchSection, PageTitleWithButton, Pagination } from '../../../components';
import { useSnackbar } from 'notistack';
import {
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Theme,
  useMediaQuery,
  Box
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link, useHistory } from 'react-router-dom';
import { Add, Edit, Send } from '@mui/icons-material';
import { getActivationNotice, getNeighborhoodNames } from '../../../fetch';
import { IActivationNotice, INeighborhood } from '../../../models';
import { MobileNeighborhoods } from './MobileNeighborhoods';
import { ActivationNoticeModal } from './ActivationNoticeModal';
import { NeighborhoodFilters } from './NeighborhoodFilters';
import { getCommonNeighborhoods } from '../../../fetch/lookups';

export const ManageNeighborhoods = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingNeighborhoods, setLoadingNeighborhoods] = useState<boolean>(true);
  const [isLoadingNeighborhoodNames, setLoadingNeighborhoodNames] = useState<boolean>(true);
  const [neighborhoodNames, setNeighborhoodNames] = useState<{ name: string }[]>([]);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [showInActive, setShowInActive] = useState(false);
  const history = useHistory();
  const [selectedName, setSelectedName] = useState<string>('');
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [isLoadingActivationNotice, setIsLoadingActivationNotice] = useState(false);
  const [activationNotice, setActivationNotice] = useState<IActivationNotice>(null);
  const [isResendActivationNoticeModalOpen, setIsResendActivationNoticeModalOpen] = useState(false);
  const [filters, setFilters] = useState<{
    name?: string;
    code?: string | number;
  }>({});
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    Code?: 'Asc' | 'Desc';
    Phase?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const fetchNeighborhoods = async () => {
    try {
      setLoadingNeighborhoods(true);
      const res = await getCommonNeighborhoods({
        page: page + 1,
        perPage: perPage,
        isActive: showInActive ? null : true,
        hasActiveJobs: false,
        search: searchedValue,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      });
      setRecordCount(res.totalRecordCount);
      setNeighborhoods(res.records);
    } catch (error) {
      enqueueSnackbar(
        error?.Detail ?? 'Error loading neighborhoods, please refresh and try again.',
        {
          variant: 'error'
        }
      );
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  const fetchNeighborhoodNames = async () => {
    setLoadingNeighborhoodNames(true);
    try {
      const res = await getNeighborhoodNames();
      setNeighborhoodNames(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingNeighborhoodNames(false);
    }
  };

  const fetchActivationNotice = async neighborhoodId => {
    setIsLoadingActivationNotice(true);
    try {
      const res = await getActivationNotice(neighborhoodId);
      setActivationNotice(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingActivationNotice(false);
    }
  };

  useEffect(() => {
    fetchNeighborhoodNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchNeighborhoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, showInActive, searchedValue, selectedSort, sortDirection, filters]);

  const handleEditNeighborhood = neighborhood => {
    history.push(`/development/neighborhoods/${neighborhood.neighborhoodId}/general-information`);
  };

  const resendActivationNotice = neighborhood => {
    fetchActivationNotice(neighborhood.neighborhoodId);
    setIsResendActivationNoticeModalOpen(true);
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Subdivision Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Code',
        accessor: 'neighborhoodCode',
        isServerSorted: selectedSort === 'Code',
        isServerSortedDesc: sortDirection.Code === 'Desc',
        handleClickColumn: () => handleClickColumn('Code')
      },
      {
        Header: 'Phase / Block Code',
        accessor: 'phase',
        isServerSorted: selectedSort === 'Phase',
        isServerSortedDesc: sortDirection.Phase === 'Desc',
        handleClickColumn: () => handleClickColumn('Phase')
      },
      {
        Header: 'Active',
        accessor: 'isActive',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return <span>{original?.isActive ? 'True' : 'False'}</span>;
        }
      },
      {
        Header: 'Notice',
        accessor: 'notice',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <>
              <Button
                id="resend"
                color="primary"
                startIcon={<Send />}
                onClick={async () => {
                  resendActivationNotice(original);
                }}
              >
                Resend
              </Button>
              <Link
                className={classes.link}
                to={`/development/neighborhoods/${original.neighborhoodId}/general-information`}
              >
                <Button id="edit" color="primary" startIcon={<Edit />}>
                  Edit
                </Button>
              </Link>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoods]);

  return (
    <Page title="Manage Neighborhoods">
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Manage Neighborhoods'
        }}
        button={
          <Box display="flex">
            <FormControlLabel
              className="print--none"
              control={
                <Switch
                  color="primary"
                  checked={showInActive}
                  id="active-inactive"
                  disabled={isLoadingNeighborhoods}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setShowInActive(e.target.checked);
                  }}
                />
              }
              label="Show Inactive"
            />
            <Divider orientation="vertical" flexItem sx={{ margin: '0 1rem 0 0' }} />
            <Link
              className={classes.link}
              to={`/development/neighborhoods/add/general-information`}
            >
              <Button
                id="create-neighborhood"
                startIcon={<Add />}
                color="primary"
                variant="contained"
                className={classes.addNeighborhoodButton}
              >
                Add Neighborhood
              </Button>
            </Link>
          </Box>
        }
      />
      <NeighborhoodFilters
        isLoading={isLoadingNeighborhoods || isLoadingNeighborhoodNames}
        handleSearch={(clearSearch?: boolean) => {
          setPage(0);
        }}
        neighborhoodNames={neighborhoodNames}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              name: '',
              code: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              name: selectedName
            });
          }
        }}
        searchValue={searchValue}
        setSearch={setSearch}
        setSearchedValue={setSearchedValue}
        setPage={setPage}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
        selectedName={selectedName}
        setSelectedName={setSelectedName}
      />
      {searchedValue && (
        <SearchSection
          searchedValue={searchedValue}
          handleClearSearch={() => {
            setSearch('');
            setSearchedValue('');
            setHasAppliedFilters(false);
            setPage(0);
          }}
        />
      )}
      <Table
        columns={columns}
        data={neighborhoods}
        isLoading={isLoadingNeighborhoods}
        hidePagination
        mobileProps={{
          handleEdit: (neighborhood: any) => handleEditNeighborhood(neighborhood),
          handleResend: (neighborhood: any) => resendActivationNotice(neighborhood)
        }}
        ResponsiveComponent={isMobile ? MobileNeighborhoods : undefined}
      />
      {!isLoadingNeighborhoods && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      {activationNotice && !isLoadingActivationNotice && (
        <ActivationNoticeModal
          activationNotice={activationNotice}
          open={isResendActivationNoticeModalOpen}
          onClose={() => {
            setIsResendActivationNoticeModalOpen(false);
            setActivationNotice(null);
          }}
          fetchNeighborhoods={fetchNeighborhoods}
        />
      )}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addNeighborhoodWrapper: {
    marginBottom: theme.spacing(1)
  },
  addNeighborhoodButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },

  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
